import type { ResourcePermissions } from 'shared'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'

/**
 * Composable for checking resource-level permissions
 *
 * Resource permissions are attached by the backend interceptor
 * based on Cerbos policy evaluation for the current user
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * const user = ref<MergedUserListItem>({
 *   id: '1',
 *   permissions: { update: true, delete: false }
 * })
 * const { canUpdate, canDelete } = useResourcePermissions(user)
 * </script>
 *
 * <template>
 *   <button v-if="canUpdate">Edit</button>
 *   <button v-if="canDelete">Delete</button>
 * </template>
 * ```
 */
export const useResourcePermissions = <T extends { permissions?: ResourcePermissions }>(
  resource: ComputedRef<T | null | undefined> | T | null | undefined,
) => {
  /**
   * Get permissions object from resource
   */
  const permissions = computed<ResourcePermissions>(() => {
    const res =
      typeof resource === 'object' && resource !== null && 'value' in resource
        ? resource.value
        : resource
    return res?.permissions ?? {}
  })

  /**
   * Check if a specific action is allowed
   * Returns false if permissions are not loaded or action is not permitted
   */
  const hasPermission = (action: keyof ResourcePermissions): boolean => {
    return permissions.value[action] === true
  }

  /**
   * Common permission checks for convenience
   */
  const canCreate = computed(() => hasPermission('create'))
  const canGet = computed(() => hasPermission('get'))
  const canUpdate = computed(() => hasPermission('update'))
  const canDelete = computed(() => hasPermission('delete'))
  const canList = computed(() => hasPermission('list'))
  const canInvite = computed(() => hasPermission('invite'))
  const canView = computed(() => hasPermission('view'))

  return {
    permissions,
    hasPermission,
    canCreate,
    canGet,
    canUpdate,
    canDelete,
    canList,
    canInvite,
    canView,
  }
}
